import {
  isDirectPublisher,
  isNetworkQuestionPublisher,
  isNetworkQuestionReplyPublisher,
  isEditPublisher as isPublisherForEditing,
  isEventPublisher as isPublisherForEvents,
  isTeamsMeetingPublisher as isPublisherForTeamsMeetings,
  isReplyPublisher,
  isUserPublisher,
} from '../../state/publishers/types';
import type { EditMessagePublisher, Publisher, User, YammerState } from '../../state/types';
import { findByIdThrows as findMessageThrows } from '../messages/selectors';
import { isNetworkQuestionsEnabledAndLicensed } from '../networkQuestions/selectors';
import {
  findById as findThreadById,
  findByIdThrows as findThreadByIdThrows,
  isDirectMessage,
  isUserStorylineThread,
} from '../threads/selectors';

import { findById, findByIdThrows } from './findPublisherSelectors';
import { getEditPublisherScope, isEditingReply } from './publisherTypeSelectors';

type IsEditTeamsMeeting = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEditTeamsMeeting: IsEditTeamsMeeting = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);
  if (!publisher.isEditing) {
    return false;
  }
  const threadScope = getEditPublisherScope(state, publisherId);
  const isTeamsMeetingThreadScope = threadScope?.subtype === 'TeamsMeetingScope';

  return isTeamsMeetingThreadScope;
};

type IsEditingTeamsMeetingQuestion = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEditingTeamsMeetingQuestion: IsEditingTeamsMeetingQuestion = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isEditTeamsMeeting(state, publisherId)) {
    return false;
  }

  if (isEditingReply(state, publisherId)) {
    return false;
  }

  return findMessageThrows(state, (publisher as EditMessagePublisher).messageId).isQuestion;
};

type IsTeamsMeetingPublisher = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isTeamsMeetingPublisher: IsTeamsMeetingPublisher = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  return isPublisherForTeamsMeetings(publisher);
};

type IsNetworkQuestionPostType = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isNetworkQuestionPostType: IsNetworkQuestionPostType = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (publisher == null) {
    return false;
  }

  return isNetworkQuestionPublisher(publisher);
};

type IsNetworkQuestionReply = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isNetworkQuestionReply: IsNetworkQuestionReply = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (publisher == null) {
    return false;
  }

  return isNetworkQuestionReplyPublisher(publisher);
};

type IsEditingAnswersCommunityQuestion = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEditingAnswersCommunityQuestion: IsEditingAnswersCommunityQuestion = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (
    !isPublisherForEditing(publisher) ||
    isEditingReply(state, publisherId) ||
    !isNetworkQuestionsEnabledAndLicensed(state)
  ) {
    return false;
  }

  const { threadId, isQuestion } = findMessageThrows(state, publisher.messageId);
  const { groupId } = findThreadByIdThrows(state, threadId);

  return !!groupId && (isQuestion || !!publisher.shouldShowTitle);
};

type IsEditingDirectMessage = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEditingDirectMessage: IsEditingDirectMessage = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  if (!isPublisherForEditing(publisher)) {
    return false;
  }

  const { messageId } = publisher;
  const { threadId } = findMessageThrows(state, messageId);

  return isDirectMessage(state, threadId);
};

type IsDirect = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isDirect: IsDirect = (state, publisherId) => {
  const { destination } = findByIdThrows(state, publisherId);

  return destination === 'Direct' || isEditingDirectMessage(state, publisherId);
};

type IsGroupOrEventThreadStarterPublisher = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isGroupOrEventThreadStarterPublisher: IsGroupOrEventThreadStarterPublisher = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  return !(isPublisherForEditing(publisher) || isReplyPublisher(publisher) || isDirectPublisher(publisher));
};

type IsEventPublisher = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEventPublisher: IsEventPublisher = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  return isPublisherForEvents(publisher);
};

type IsEditingUserStorylineThreadStarter = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEditingUserStorylineThreadStarter: IsEditingUserStorylineThreadStarter = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  return (
    isStorylinePublisher(state, publisherId) && isPublisherForEditing(publisher) && !isEditingReply(state, publisherId)
  );
};

type IsReplyToUserStorylineThread = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isReplyToUserStorylineThread: IsReplyToUserStorylineThread = (state, publisherId) => {
  const publisher = findByIdThrows(state, publisherId);

  return isReplyPublisher(publisher) && isUserStorylineThread(state, publisher.threadId);
};

type IsEditingReplyToUserStorylineThread = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isEditingReplyToUserStorylineThread: IsEditingReplyToUserStorylineThread = (state, publisherId) =>
  isStorylinePublisher(state, publisherId) && isEditingReply(state, publisherId);

type GetUserId = (state: YammerState, publisherId: Publisher['id']) => User['id'] | undefined;
export const getUserId: GetUserId = (state, publisherId) => {
  const publisher = findById(state, publisherId);

  if (!publisher) {
    return;
  }

  if (isUserPublisher(publisher)) {
    return publisher.userId;
  }

  if (isPublisherForEditing(publisher)) {
    const { threadId } = findMessageThrows(state, publisher.messageId);
    const thread = findThreadById(state, threadId);

    return thread?.wallOwnerId;
  }

  return;
};

type IsStorylinePublisher = (state: YammerState, publisherId: Publisher['id']) => boolean;
export const isStorylinePublisher: IsStorylinePublisher = (state, publisherId) => !!getUserId(state, publisherId);
